import { useCallback, useEffect, useState, useMemo, ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { css, cx } from '@emotion/css';
import { Dropdown as AntdDropdown, ConfigProvider, DropDownProps, Typography } from 'antd';
import locale from 'antd/locale/ko_KR';

import { Button } from './Button';
import { Flex } from './Flex';
import { KeyboardArrowRight } from './Icon';
import { Colors } from '../colors';
import { themeForClobe } from '../style/constant';
import { typographyStyle } from '../style/typography';

interface Props extends DropDownProps {
  disableScroll?: boolean; // 외부 스크롤 방지 및 dimmed 적용 여부
  openTrigger?: 'click' | 'hover' | 'contextMenu';
  autoClose?: boolean; // 메뉴 아이템 선택 시 자동으로 닫힘 여부
  onDropdownOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  isOpen?: boolean;
  setIsOpen?: (open: boolean) => void;
}

const openDropdowns: Set<() => void> = new Set(); // 열린 Dropdown들을 추적하는 Set

export const Dropdown: React.FC<Props> = ({
  disableScroll = false,
  openTrigger = 'click',
  onDropdownOpenChange,
  defaultOpen,
  menu,
  autoClose = true,
  isOpen: isOpenProp,
  setIsOpen: setIsOpenProp,
  ...props
}) => {
  const [defaultIsOpen, setDefaultIsOpen] = useState(defaultOpen ?? false);
  const isOpen = isOpenProp ?? defaultIsOpen;
  // 현재 open 상태와 setter 결정
  const setIsOpen = useCallback(
    (open: boolean) => {
      if (setIsOpenProp) {
        setIsOpenProp(open);
      } else {
        setDefaultIsOpen(open);
      }
    },
    [setIsOpenProp],
  );

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    onDropdownOpenChange?.(false);
  }, [onDropdownOpenChange, setIsOpen]);

  useEffect(() => {
    if (disableScroll && isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [disableScroll, isOpen]);

  useEffect(() => {
    if (!defaultOpen) {
      setIsOpen(false);
    }
  }, [defaultOpen]);

  useEffect(() => {
    if (isOpen) {
      openDropdowns.add(closeDropdown);
    } else {
      openDropdowns.delete(closeDropdown);
    }
    return () => {
      openDropdowns.delete(closeDropdown);
    };
  }, [isOpen, closeDropdown]);

  const closeTopDropdown = useCallback(() => {
    const closeFn = Array.from(openDropdowns).pop();
    if (closeFn) closeFn();
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        event.preventDefault();
        closeTopDropdown();
      }
    },
    [closeTopDropdown],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  const handleDimmedClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDropdownOpenChange?.(false);
      closeTopDropdown();
    },
    [onDropdownOpenChange, closeTopDropdown],
  );

  const dimmedStyle = useMemo(
    () => css`
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: transparent;
      z-index: 1020;
    `,
    [],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      Promise.resolve().then(() => {
        onDropdownOpenChange?.(open);
        setIsOpen(open);
      });
    },
    [onDropdownOpenChange, setIsOpen],
  );

  const handleMenuClick = useCallback(() => {
    if (autoClose) {
      closeDropdown();
    }
  }, [closeDropdown, autoClose]);

  const menuWithClose = useMemo(() => {
    if (!menu) return undefined;
    return {
      ...menu,
      onClick: (info: any) => {
        menu.onClick?.(info);
        handleMenuClick();
      },
    };
  }, [menu, handleMenuClick]);

  return (
    <>
      {disableScroll &&
        isOpen &&
        createPortal(<div className={dimmedStyle} onClick={handleDimmedClick} />, document.body)}
      <ConfigProvider locale={locale} theme={{ ...themeForClobe }}>
        <AntdDropdown
          {...props}
          open={isOpen}
          trigger={[openTrigger]}
          onOpenChange={handleOpenChange}
          destroyPopupOnHide={true}
          transitionName=""
          menu={menuWithClose}
        />
      </ConfigProvider>
    </>
  );
};

interface ButtonProps extends Props {
  title: string | ReactNode;
  buttonProps?: React.ComponentProps<typeof Button>;
  menuProps?: {
    className?: string;
  };
}

export const DropdownButton: React.FC<ButtonProps> = ({ ...props }) => {
  const buttonStyle = useMemo(
    () => css`
      padding: 6px;
    `,
    [],
  );

  const menuStyle = useMemo(
    () => css`
      &&& .ant-dropdown-menu-item {
        padding: 6px 12px;
      }
      &&& .ant-dropdown-menu-item-selected {
        background-color: white;
      }
      &&& .ant-dropdown-menu-title-content {
        font-size: 14px;
        ${typographyStyle['14_400']}
      }
    `,
    [],
  );

  return (
    <Dropdown
      {...props}
      disableScroll
      className={cx(buttonStyle, props.className)}
      dropdownRender={(menu) => (
        <div className={cx(menuStyle, props.menuProps?.className)}>{menu}</div>
      )}
    >
      <Button border {...props.buttonProps}>
        <Flex
          align="center"
          justify="space-between"
          style={{
            width: '100%',
          }}
        >
          {typeof props.title === 'string' ? (
            <Typography.Text
              style={{
                ...typographyStyle['14_400'],
                color: Colors.gray700,
                width: 'fit-content',
              }}
              ellipsis
            >
              {props.title}
            </Typography.Text>
          ) : (
            props.title
          )}

          <KeyboardArrowRight
            width={18}
            height={18}
            style={{
              rotate: '90deg',
            }}
          />
        </Flex>
      </Button>
    </Dropdown>
  );
};
